/**
 * Outil « Géoplan : aires et périmètres » (Boîte à outils, 6e → 4e).
 *
 * Construire : on touche des clous pour poser les sommets d'un polygone ; aire
 * exacte, périmètre, carreaux entiers et entamés. Formules : rectangle,
 * triangle (doublé en parallélogramme), parallélogramme (découpé en
 * rectangle), disque (π r² contre les carreaux), avec des poignées.
 */

import {
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type SVGProps,
} from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { makeFrame } from '../../ui/frame';
import { DragHandle, FIGURE_BG_CLASS, HaloText, INK, RightAngleMark } from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import {
  N,
  area,
  cells,
  fmt2,
  polygonCells,
  countCells,
  fmt,
  isSimple,
  openIsSimple,
  perimeter,
  sameAreaRectangles,
  sides,
  type CellKind,
  type Pt,
} from './geo';

const SIZE = 320;
const VP = { xMin: -0.5, xMax: N + 0.5, yMin: -0.5, yMax: N + 0.5 };
const frame = makeFrame(VP, SIZE);
const FILL = 'var(--color-primary-500)';
const GHOST = 'var(--color-accent-500)';
const P = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
const poly = (pts: readonly Pt[]) => pts.map((p) => `${P(p).x},${P(p).y}`).join(' ');
const clampI = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)));
const ptText = (p: Pt) => `(${p.x} ; ${p.y})`;

function Board({
  svgRef,
  label,
  svgProps,
  cellMap,
  children,
}: {
  svgRef?: RefObject<SVGSVGElement | null>;
  label: string;
  svgProps?: SVGProps<SVGSVGElement>;
  cellMap?: Map<string, CellKind> | null;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-[26rem]">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className={cn(
          'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
          FIGURE_BG_CLASS,
        )}
        role="group"
        aria-label={label}
        {...svgProps}
      >
        {cellMap &&
          [...cellMap.entries()].map(([k, v]) => {
            if (v === 'out') return null;
            const [i, j] = k.split(',').map(Number) as [number, number];
            return (
              <rect
                key={k}
                x={frame.tx(i)}
                y={frame.ty(j + 1)}
                width={frame.unit}
                height={frame.unit}
                fill={v === 'full' ? 'var(--color-success-500)' : 'var(--color-accent-500)'}
                fillOpacity={v === 'full' ? 0.28 : 0.18}
                aria-hidden
              />
            );
          })}
        {Array.from({ length: N + 1 }, (_, i) => (
          <g key={i} aria-hidden>
            <line
              x1={frame.tx(i)}
              y1={frame.ty(0)}
              x2={frame.tx(i)}
              y2={frame.ty(N)}
              stroke="var(--color-ink-100)"
            />
            <line
              x1={frame.tx(0)}
              y1={frame.ty(i)}
              x2={frame.tx(N)}
              y2={frame.ty(i)}
              stroke="var(--color-ink-100)"
            />
          </g>
        ))}
        {Array.from({ length: (N + 1) * (N + 1) }, (_, k) => (
          <circle
            key={k}
            cx={frame.tx(k % (N + 1))}
            cy={frame.ty(Math.floor(k / (N + 1)))}
            r={2.5}
            fill="var(--color-ink-400)"
            aria-hidden
          />
        ))}
        {children}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* construire                                                          */
/* ------------------------------------------------------------------ */

function BuildMode({ rank, onSend, sendCost }: { rank: number } & ToolProps) {
  const [pts, setPts] = useState<Pt[]>([]);
  const [closed, setClosed] = useState(false);
  const [cursor, setCursor] = useState<Pt>({ x: 4, y: 4 });
  const [count, setCount] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // dernière action au clavier : annoncer le curseur
  const [moved, setMoved] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const tap = (raw: Pt) => {
    setMoved(false);
    const p = { x: clampI(raw.x, 0, N), y: clampI(raw.y, 0, N) };
    setCursor(p);
    if (closed) return;
    if (pts.length >= 3 && p.x === pts[0]!.x && p.y === pts[0]!.y) return close();
    if (pts.some((q) => q.x === p.x && q.y === p.y)) {
      setMsg('Ce clou est déjà un sommet.');
      return;
    }
    const next = [...pts, p];
    // pas de côté qui en coupe un autre (on vérifie la ligne brisée)
    if (next.length >= 3 && !openIsSimple(next)) {
      setMsg('Ce côté couperait un autre côté : choisis un autre clou.');
      return;
    }
    setMsg(null);
    setPts(next);
  };

  const close = () => {
    if (pts.length < 3) return;
    if (!isSimple(pts)) {
      setMsg('En fermant, deux côtés se couperaient (ou la figure serait plate).');
      return;
    }
    setMsg(null);
    setClosed(true);
  };

  const drag = useDrag<never>({
    svgRef,
    frame,
    ids: [],
    pointOf: () => ({ x: 0, y: 0 }),
    onDrag: () => {},
    onBackground: (p, phase) => {
      if (phase === 'start') tap(p);
    },
  });

  const onKey = (e: KeyboardEvent<SVGGElement>) => {
    const d: Record<string, [number, number]> = {
      ArrowRight: [1, 0],
      ArrowLeft: [-1, 0],
      ArrowUp: [0, 1],
      ArrowDown: [0, -1],
    };
    const m = d[e.key];
    if (m) {
      e.preventDefault();
      setCursor((c) => ({ x: clampI(c.x + m[0], 0, N), y: clampI(c.y + m[1], 0, N) }));
      setMoved(true);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      tap(cursor);
    }
  };

  const a = closed ? area(pts) : 0;
  const per = closed ? perimeter(pts) : null;
  const ss = closed ? sides(pts) : [];
  const cellMap = closed && count ? polygonCells(pts) : null;
  const cc = cellMap ? countCells(cellMap) : null;

  return (
    <div className="space-y-4">
      <Board
        svgRef={svgRef}
        label={`Géoplan : ${pts.length} sommet${pts.length > 1 ? 's' : ''}${closed ? ', figure fermée' : ''}`}
        svgProps={drag.svgProps}
        cellMap={cellMap}
      >
        {closed ? (
          <polygon
            points={poly(pts)}
            fill={FILL}
            fillOpacity={count ? 0.06 : 0.18}
            stroke={FILL}
            strokeWidth={3}
            strokeLinejoin="round"
            aria-hidden
          />
        ) : (
          pts.length > 1 && (
            <polyline
              points={poly(pts)}
              fill="none"
              stroke={FILL}
              strokeWidth={3}
              strokeLinejoin="round"
              aria-hidden
            />
          )
        )}
        {pts.map((p, i) => (
          <circle
            key={i}
            cx={P(p).x}
            cy={P(p).y}
            r={i === 0 && !closed ? 8 : 6}
            fill={i === 0 && !closed ? 'var(--color-accent-500)' : FILL}
            aria-hidden
          />
        ))}
        <g
          role="button"
          tabIndex={0}
          aria-label={`Curseur sur le clou ${ptText(cursor)}. Flèches pour le déplacer, Entrée pour poser un sommet.`}
          onKeyDown={onKey}
          className="outline-none [&:focus-visible_circle]:opacity-100"
        >
          <circle
            cx={P(cursor).x}
            cy={P(cursor).y}
            r={13}
            fill="none"
            stroke="var(--color-primary-400)"
            strokeWidth={2.5}
            opacity={0}
          />
        </g>
      </Board>

      <div className="flex flex-wrap gap-2">
        {!closed && pts.length >= 3 && (
          <button type="button" className={SECONDARY_BUTTON} onClick={close}>
            Fermer la figure
          </button>
        )}
        <button
          type="button"
          className={SECONDARY_BUTTON}
          disabled={pts.length === 0}
          onClick={() => {
            if (closed) setClosed(false);
            else setPts((q) => q.slice(0, -1));
            setMsg(null);
          }}
        >
          Annuler
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          disabled={pts.length === 0}
          onClick={() => {
            setPts([]);
            setClosed(false);
            setMsg(null);
          }}
        >
          Effacer
        </button>
        {closed && (
          <ToggleChip on={count} onClick={() => setCount((v) => !v)}>
            Compter les carreaux
          </ToggleChip>
        )}
      </div>

      <p className="sr-only" aria-live="polite">
        {msg ??
          (moved
            ? `Curseur ${ptText(cursor)}`
            : closed
              ? `Figure fermée : aire ${fmt(a)}, périmètre ${per!.text}`
              : pts.length
                ? `Sommets : ${pts.map(ptText).join(', ')}`
                : '')}
      </p>
      {msg && (
        <Note tone="error" live={false}>
          {frenchSpaces(msg)}
        </Note>
      )}
      {!closed && !msg && (
        <Note live={false}>
          {frenchSpaces(
            pts.length < 3
              ? 'Touche les clous un par un pour poser les sommets.'
              : 'Touche le premier sommet (orange) ou « Fermer la figure ».',
          )}
        </Note>
      )}

      <Legend />
      {closed && per && (
        <section
          className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
          data-testid="gb-build"
        >
          <div className="flex min-h-11 items-center gap-2">
            <MathText className="text-ink-900 min-w-0 flex-1 text-lg">
              {`$\\mathcal{A} = ${fmt(a).replace(',', '{,}')}\\ \\text{u.a.}$`}
            </MathText>
            {onSend && (
              <SendToSheetButton
                label={fmt(a)}
                cost={sendCost}
                onClick={() => onSend({ atoms: [fmt(a)] })}
              />
            )}
          </div>
          {cc && (
            <p className="text-ink-700 text-sm">
              {frenchSpaces(
                `${cc.full} carreau${cc.full > 1 ? 'x' : ''} entier${cc.full > 1 ? 's' : ''} (en vert) et ${cc.part} carreau${cc.part > 1 ? 'x' : ''} entamé${cc.part > 1 ? 's' : ''} (en jaune) : les morceaux se complètent pour faire ${fmt(a - cc.full)} carreau${a - cc.full > 1 ? 'x' : ''} de plus.`,
              )}
            </p>
          )}
          <p className="text-ink-800 text-sm">
            {frenchSpaces(
              per.exact
                ? `Périmètre = ${per.text} u.l. : ${ss.map((x) => x.plain).join(' + ')}.`
                : rank >= 3
                  ? `Périmètre = ${ss.map((x) => x.plain).join(' + ')} ≈ ${fmt2(per.value)} u.l.`
                  : `Périmètre ≈ ${fmt2(per.value)} u.l. : ${ss.map((x) => (x.straight ? x.plain : fmt2(x.value))).join(' + ')} (côtés obliques arrondis au centième).`,
            )}
          </p>
          {ss.some((x) => !x.straight) && (
            <p className="text-ink-600 text-sm">
              {frenchSpaces(
                rank >= 3
                  ? 'Un côté oblique est l’hypoténuse d’un triangle rectangle de côtés a et b carreaux : √(a² + b²), par Pythagore.'
                  : 'Un côté oblique ne se compte pas en carreaux : on le mesure à la règle (valeur approchée).',
              )}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* formules                                                            */
/* ------------------------------------------------------------------ */

type Shape = 'rect' | 'triangle' | 'para' | 'disk';
type H = 'a' | 'b' | 'c';

function FormulaMode({ rank, onSend, sendCost }: { rank: number } & ToolProps) {
  const [shape, setShape] = useState<Shape>('rect');
  const [rect, setRect] = useState({ w: 4, h: 3 });
  const [tri, setTri] = useState({ b: 4, apex: { x: 3, y: 5 } });
  const [para, setPara] = useState({ b: 4, top: { x: 2, y: 4 } });
  const [r, setR] = useState(3);
  const [extra, setExtra] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const O = { x: 1, y: 1 };

  const handles: Record<Shape, H[]> = {
    rect: ['a'],
    triangle: ['a', 'b'],
    para: ['a', 'b'],
    disk: ['a'],
  };
  const pointOf = (h: H): Pt => {
    if (shape === 'rect') return { x: 1 + rect.w, y: 1 + rect.h };
    if (shape === 'triangle') return h === 'a' ? { x: 1 + tri.b, y: 1 } : tri.apex;
    if (shape === 'para') return h === 'a' ? { x: 1 + para.b, y: 1 } : para.top;
    return { x: 4 + r, y: 4 };
  };
  const drag = useDrag<H>({
    svgRef,
    frame,
    ids: handles[shape],
    pointOf,
    onDrag: (h, p) => {
      if (shape === 'rect') setRect({ w: clampI(p.x - 1, 1, 7), h: clampI(p.y - 1, 1, 7) });
      else if (shape === 'triangle') {
        if (h === 'a') setTri((t) => ({ ...t, b: clampI(p.x - 1, 1, 7) }));
        else setTri((t) => ({ ...t, apex: { x: clampI(p.x, 0, N), y: clampI(p.y, 2, N) } }));
      } else if (shape === 'para') {
        if (h === 'a')
          setPara((q) => ({ ...q, b: clampI(p.x - 1, 1, 7 - Math.max(0, q.top.x - 1)) }));
        else
          setPara((q) => ({
            ...q,
            top: { x: clampI(p.x, 0, N - q.b), y: clampI(p.y, 2, N) },
          }));
      } else setR(clampI(p.x - 4, 1, 4));
    },
  });

  const shapes: Array<{ value: Shape; label: string }> = [
    { value: 'rect', label: 'Rectangle' },
    ...(rank >= 2
      ? [
          { value: 'triangle' as Shape, label: 'Triangle' },
          { value: 'para' as Shape, label: 'Parallélogramme' },
        ]
      : []),
    { value: 'disk', label: 'Disque' },
  ];

  let fig: ReactNode = null;
  let formulas: string[] = [];
  let say = '';
  let value: number | null = null;
  let extraLabel = '';
  let cellMap: Map<string, CellKind> | null = null;

  if (shape === 'rect') {
    const { w, h } = rect;
    const pts = [O, { x: 1 + w, y: 1 }, { x: 1 + w, y: 1 + h }, { x: 1, y: 1 + h }];
    value = w * h;
    fig = (
      <polygon
        points={poly(pts)}
        fill={FILL}
        fillOpacity={0.18}
        stroke={FILL}
        strokeWidth={3}
        aria-hidden
      />
    );
    formulas = [
      `\\mathcal{A} = L \\times \\ell = ${w} \\times ${h} = ${w * h}\\ \\text{u.a.}`,
      `\\mathcal{P} = 2 \\times (L + \\ell) = 2 \\times (${w} + ${h}) = ${2 * (w + h)}\\ \\text{u.l.}`,
    ];
    extraLabel = 'Même aire, autres rectangles';
    const same = sameAreaRectangles(w * h);
    say = extra
      ? same.length === 1
        ? `Avec des côtés entiers, le seul rectangle d’aire ${w * h} est ${same[0]!.w} × ${same[0]!.h} (périmètre ${same[0]!.p} u.l.). Essaie une aire comme 12 ou 24.`
        : `Rectangles d’aire ${w * h} : ${same
            .map((x) => `${x.w} × ${x.h} (périmètre ${x.p} u.l.)`)
            .join(', ')}. Même aire, mais pas le même périmètre !`
      : w === h
        ? 'Un carré : sa longueur et sa largeur sont égales au côté c, donc 𝒜 = c × c.'
        : 'Déplace le coin pour changer la longueur et la largeur.';
  } else if (shape === 'triangle') {
    const A = O;
    const B = { x: 1 + tri.b, y: 1 };
    const C = tri.apex;
    const h = C.y - 1;
    const Hf = { x: C.x, y: 1 };
    value = (tri.b * h) / 2;
    const D = { x: B.x + C.x - A.x, y: C.y };
    fig = (
      <g aria-hidden>
        {(Hf.x < A.x || Hf.x > B.x) && (
          <line
            x1={P(Hf).x}
            y1={P(Hf).y}
            x2={P(Hf.x < A.x ? A : B).x}
            y2={P(A).y}
            stroke="var(--color-ink-400)"
            strokeDasharray="4 4"
          />
        )}
        {extra && D.x <= N && (
          <polygon
            points={poly([B, D, C])}
            fill={GHOST}
            fillOpacity={0.15}
            stroke={GHOST}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}
        <polygon
          points={poly([A, B, C])}
          fill={FILL}
          fillOpacity={0.18}
          stroke={FILL}
          strokeWidth={3}
        />
        <line
          x1={P(C).x}
          y1={P(C).y}
          x2={P(Hf).x}
          y2={P(Hf).y}
          stroke={INK}
          strokeWidth={2}
          strokeDasharray="5 4"
        />
        <RightAngleMark
          x={P(Hf).x}
          y={P(Hf).y}
          ux={P(C).x > P(A).x ? -1 : 1}
          uy={0}
          vx={0}
          vy={-1}
          size={9}
        />
        <HaloText
          x={P(Hf).x + 10}
          y={(P(C).y + P(Hf).y) / 2}
          fontSize={14}
          fontWeight={700}
          fill={INK}
        >
          h
        </HaloText>
      </g>
    );
    formulas = [
      `\\mathcal{A} = \\frac{b \\times h}{2} = \\frac{${tri.b} \\times ${h}}{2} = ${fmt(value).replace(',', '{,}')}\\ \\text{u.a.}`,
    ];
    extraLabel = 'Doubler le triangle';
    say = extra
      ? D.x <= N
        ? `Deux triangles identiques forment un parallélogramme de base ${tri.b} et de hauteur ${h} : ${tri.b * h} u.a. Le triangle en est la moitié.`
        : 'Rapproche le sommet du haut de la gauche de la base pour voir le triangle doublé.'
      : 'Glisse le sommet horizontalement : la hauteur ne change pas, l’aire non plus.';
  } else if (shape === 'para') {
    const A = O;
    const B = { x: 1 + para.b, y: 1 };
    const Dp = para.top;
    const C = { x: B.x + Dp.x - A.x, y: Dp.y };
    const h = Dp.y - 1;
    const s = Dp.x - A.x;
    const Hf = { x: Dp.x, y: 1 };
    value = para.b * h;
    const canCut = s >= 0 && s <= para.b;
    fig = (
      <g aria-hidden>
        <polygon
          points={poly([A, B, C, Dp])}
          fill={FILL}
          fillOpacity={extra && canCut ? 0.06 : 0.18}
          stroke={FILL}
          strokeWidth={3}
        />
        {extra && canCut && s > 0 && (
          <>
            <polygon
              points={poly([A, Hf, Dp])}
              fill={GHOST}
              fillOpacity={0.12}
              stroke={GHOST}
              strokeDasharray="4 4"
            />
            <polygon
              points={poly([B, { x: Hf.x + para.b, y: 1 }, C])}
              fill={GHOST}
              fillOpacity={0.3}
              stroke={GHOST}
              strokeWidth={2}
            />
            <polygon
              points={poly([Hf, { x: Hf.x + para.b, y: 1 }, C, Dp])}
              fill="none"
              stroke={INK}
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          </>
        )}
        <line
          x1={P(Dp).x}
          y1={P(Dp).y}
          x2={P(Hf).x}
          y2={P(Hf).y}
          stroke={INK}
          strokeWidth={2}
          strokeDasharray="5 4"
        />
        <HaloText
          x={P(Hf).x + 10}
          y={(P(Dp).y + P(Hf).y) / 2}
          fontSize={14}
          fontWeight={700}
          fill={INK}
        >
          h
        </HaloText>
      </g>
    );
    formulas = [`\\mathcal{A} = b \\times h = ${para.b} \\times ${h} = ${value}\\ \\text{u.a.}`];
    extraLabel = 'Découper et déplacer';
    say = extra
      ? s === 0
        ? 'C’est déjà un rectangle : rien à découper. Glisse le côté du haut, puis découpe.'
        : canCut
          ? `On découpe le triangle de gauche et on le recolle à droite : un rectangle de ${para.b} sur ${h}, même aire.`
          : 'Place le haut pour que la hauteur tombe sur la base, puis découpe.'
      : 'Glisse le côté du haut : base et hauteur ne changent pas, l’aire non plus.';
  } else {
    const c = { x: 4, y: 4 };
    cellMap = extra ? cells((p) => Math.hypot(p.x - 4, p.y - 4) < r) : null;
    const cc = cellMap ? countCells(cellMap) : null;
    value = null;
    fig = (
      <g aria-hidden>
        <circle
          cx={P(c).x}
          cy={P(c).y}
          r={r * frame.unit}
          fill={FILL}
          fillOpacity={extra ? 0.06 : 0.16}
          stroke={FILL}
          strokeWidth={3}
        />
        <line
          x1={P(c).x}
          y1={P(c).y}
          x2={P({ x: 4 + r, y: 4 }).x}
          y2={P(c).y}
          stroke={INK}
          strokeWidth={2}
        />
        <HaloText
          x={(P(c).x + P({ x: 4 + r, y: 4 }).x) / 2}
          y={P(c).y - 8}
          textAnchor="middle"
          fontSize={14}
          fontWeight={700}
          fill={INK}
        >
          r
        </HaloText>
        <circle cx={P(c).x} cy={P(c).y} r={3.5} fill={INK} />
      </g>
    );
    formulas = [
      `\\mathcal{P} = 2 \\times \\pi \\times r = 2 \\times \\pi \\times ${r} \\approx ${fmt(2 * Math.PI * r).replace(',', '{,}')}\\ \\text{u.l.}`,
      ...(rank >= 2
        ? [
            `\\mathcal{A} = \\pi \\times r^2 = \\pi \\times ${r}^2 \\approx ${fmt(Math.PI * r * r).replace(',', '{,}')}\\ \\text{u.a.}`,
          ]
        : []),
    ];
    extraLabel = 'Compter les carreaux';
    say =
      extra && cc
        ? `${cc.full} carreaux entiers dans le disque, ${cc.part} entamés : l’aire est entre ${cc.full} et ${cc.full + cc.part} u.a.${rank >= 2 ? ` π × r² ≈ ${fmt(Math.PI * r * r)} tombe bien dedans.` : ''}`
        : `Le diamètre vaut 2 × r = ${2 * r} : le périmètre est un peu plus de 3 fois le diamètre (π ≈ 3,14).`;
  }

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Figure"
        value={shape}
        options={shapes}
        onChange={(v) => {
          setShape(v);
          setExtra(false);
        }}
      />
      <Board
        svgRef={svgRef}
        label={`Géoplan : ${shapes.find((s) => s.value === shape)!.label.toLowerCase()}`}
        svgProps={drag.svgProps}
        cellMap={cellMap}
      >
        {fig}
        {handles[shape].map((h) => {
          const p = P(pointOf(h));
          return (
            <DragHandle
              key={h}
              x={p.x}
              y={p.y}
              color={h === 'a' ? FILL : GHOST}
              name={
                shape === 'disk'
                  ? `Rayon r = ${r}`
                  : h === 'a'
                    ? `Coin ${ptText(pointOf(h))}`
                    : `Sommet du haut ${ptText(pointOf(h))}`
              }
              active={drag.active === h}
              keyProps={drag.keyProps(h)}
            />
          );
        })}
      </Board>
      <ToggleChip on={extra} onClick={() => setExtra((v) => !v)}>
        {extraLabel}
      </ToggleChip>
      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="gb-formula"
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            {formulas.map((f) => (
              <MathText key={f} className="text-ink-900 block overflow-x-auto">{`$${f}$`}</MathText>
            ))}
          </div>
          {onSend && value !== null && (
            <SendToSheetButton
              label={fmt(value)}
              cost={sendCost}
              onClick={() => onSend({ atoms: [fmt(value!)] })}
            />
          )}
        </div>
      </section>
      <Note live={false}>{frenchSpaces(say)}</Note>
      <Legend />
    </div>
  );
}

function Legend() {
  return (
    <p className="text-ink-500 text-xs">
      {frenchSpaces(
        'u.a. : unité d’aire (un carreau) ; u.l. : unité de longueur (un côté de carreau).',
      )}
    </p>
  );
}

export function GeoboardTool({ onSend, sendCost, level }: ToolProps & { level?: string | null }) {
  const rank = Math.min(3, Math.max(1, schoolLevelRank(level) ?? 3));
  const [mode, setMode] = useState<'build' | 'formula'>('build');
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'build', label: 'Construire' },
          { value: 'formula', label: 'Formules' },
        ]}
        onChange={setMode}
      />
      {mode === 'build' ? (
        <BuildMode rank={rank} onSend={onSend} sendCost={sendCost} />
      ) : (
        <FormulaMode rank={rank} onSend={onSend} sendCost={sendCost} />
      )}
    </div>
  );
}
